import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CustomerProvider } from "@/context/CustomerContext";
import { CartProvider } from "@/context/CartContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollManager from "@/components/ScrollManager";

import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";

const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const Donate = lazy(() => import("@/pages/Donate"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const Login = lazy(() => import("@/pages/Login"));
const Admin = lazy(() => import("@/pages/Admin"));
const About = lazy(() => import("@/pages/About"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Quiz = lazy(() => import("@/pages/Quiz"));
const Submit = lazy(() => import("@/pages/Submit"));
const Lounge = lazy(() => import("@/pages/Lounge"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderConfirm = lazy(() => import("@/pages/OrderConfirm"));
const Account = lazy(() => import("@/pages/account/Account"));
const AccountLogin = lazy(() => import("@/pages/account/AccountLogin"));
const ForgotPassword = lazy(() => import("@/pages/account/AccountRecovery").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/account/AccountRecovery").then((m) => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import("@/pages/account/AccountRecovery").then((m) => ({ default: m.VerifyEmail })));
const UnsubscribeRestock = lazy(() => import("@/pages/account/AccountRecovery").then((m) => ({ default: m.UnsubscribeRestock })));
const SharedWishlist = lazy(() => import("@/pages/SharedWishlist"));
const GiftGuide = lazy(() => import("@/pages/GiftGuide"));
const CollectionLanding = lazy(() => import("@/pages/CollectionLanding"));

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <CartSheet />
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CustomerProvider>
        <SiteSettingsProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollManager />
              <Suspense fallback={<div data-testid="route-loading" className="min-h-[60vh]" />}>
              <Routes>
                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                <Route path="/shop" element={<PublicLayout><Catalog /></PublicLayout>} />
                <Route path="/shop/:slug" element={<PublicLayout><CollectionLanding /></PublicLayout>} />
                <Route path="/product/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
                <Route path="/quiz" element={<PublicLayout><Quiz /></PublicLayout>} />
                <Route path="/submit" element={<PublicLayout><Submit /></PublicLayout>} />
                <Route path="/lounge" element={<PublicLayout><Lounge /></PublicLayout>} />
                <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
                <Route path="/donate" element={<PublicLayout><Donate /></PublicLayout>} />
                <Route path="/reviews" element={<PublicLayout><Reviews /></PublicLayout>} />
                <Route path="/account" element={<PublicLayout><Account /></PublicLayout>} />
                <Route path="/account/login" element={<PublicLayout><AccountLogin /></PublicLayout>} />
                <Route path="/account/forgot" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
                <Route path="/account/reset" element={<PublicLayout><ResetPassword /></PublicLayout>} />
                <Route path="/account/verify" element={<PublicLayout><VerifyEmail /></PublicLayout>} />
                <Route path="/account/unsubscribe" element={<PublicLayout><UnsubscribeRestock /></PublicLayout>} />
                <Route path="/wishlist/:token" element={<PublicLayout><SharedWishlist /></PublicLayout>} />
                <Route path="/gifts/:slug" element={<PublicLayout><GiftGuide /></PublicLayout>} />
                <Route path="/track" element={<PublicLayout><TrackOrder /></PublicLayout>} />
                <Route path="/order/:paypalOrderId" element={<PublicLayout><OrderConfirm /></PublicLayout>} />
                <Route path="/login" element={<Login />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </Suspense>
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
          </CartProvider>
        </SiteSettingsProvider>
        </CustomerProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
